import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient, aiApi } from "../api/client";
import type { EvaluationCriteria, TeamEvaluationDetail, Team, AiInsight } from "../api/types";
import Card from "../components/Card";
import { useAuth } from "../context/AuthContext";
import { exportToCSV, exportToPDF, prepareDetailedEvaluationForExport } from "../utils/export";

function TeamEvaluationReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState<TeamEvaluationDetail | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [ai, setAi] = useState<AiInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Fetch evaluation, team, and criteria
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const evaluationRes = await apiClient.get<TeamEvaluationDetail>(
          `/team-evaluations/id/${id}`
        );
        setEvaluation(evaluationRes as TeamEvaluationDetail);

        const [teamRes, criteriaRes] = await Promise.all([
          apiClient.get<Team>(`/teams/${evaluationRes.teamId}`),
          apiClient.get<{ criteria: EvaluationCriteria[] }>("/evaluation-criteria"),
        ]);

        setTeam(teamRes as Team);
        setCriteria(criteriaRes.criteria || []);
        setError(null);
      } catch (err: any) {
        setError(err.message || "Failed to load team evaluation report");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const criteriaMap = useMemo(() => {
    const map: Record<string, EvaluationCriteria> = {};
    criteria.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [criteria]);

  const averageScore = useMemo(() => {
    if (!evaluation || !evaluation.scores) return null;
    const values = Object.values(evaluation.scores || {});
    if (!values.length) return null;
    return Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2));
  }, [evaluation]);

  const scoreDifference = useMemo(() => {
    if (!evaluation?.overallScore || !evaluation?.individualAvgScore) return null;
    return Number((evaluation.overallScore - evaluation.individualAvgScore).toFixed(2));
  }, [evaluation]);

  const loadAi = async () => {
    if (!evaluation) return;
    try {
      setAiLoading(true);
      setAiError(null);
      const insight = await aiApi.summarizeTeam(evaluation.teamId);
      setAi(insight);
    } catch (err: any) {
      setAiError(err.message || "Failed to load AI insight");
    } finally {
      setAiLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (!evaluation || !team) return;
    exportToPDF(`Team_Evaluation_${team.name}_${new Date().toISOString().split("T")[0]}`);
  };

  const handleExportCSV = () => {
    if (!evaluation || !team) return;
    const data = prepareDetailedEvaluationForExport(evaluation, criteria, "team");
    exportToCSV(data, `Team_Evaluation_${team.name}_${new Date().toISOString().split("T")[0]}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading team evaluation report...</div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error || "Team evaluation not found"}
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      case "SUBMITTED":
        return "bg-blue-100 text-blue-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Team Evaluation Report</h2>
          <p className="text-sm text-slate-500 mt-1">
            {team?.name || "Team"} - Performance overview
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors print:hidden"
            title="Export to CSV"
          >
            📊 CSV
          </button>
          <button
            onClick={handleExportPDF}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors print:hidden"
            title="Export to PDF"
          >
            📄 PDF
          </button>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(evaluation.status)}`}
          >
            {evaluation.status}
          </span>
        </div>
      </div>

      {/* Summary */}
      <Card title="Summary">
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Team</p>
            <p className="font-semibold text-slate-800">{team?.name || evaluation.teamId}</p>
            {team?.description && <p className="text-xs text-slate-600 mt-1">{team.description}</p>}
          </div>
          <div>
            <p className="text-slate-500">Evaluator ID</p>
            <p className="font-mono text-slate-800">{evaluation.evaluatorId}</p>
          </div>
          <div>
            <p className="text-slate-500">Period</p>
            <p className="text-slate-800">{evaluation.periodId}</p>
          </div>
          <div>
            <p className="text-slate-500">Created</p>
            <p className="text-slate-800">{new Date(evaluation.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-slate-500">Submitted</p>
            <p className="text-slate-800">
              {evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "-"}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Completed</p>
            <p className="text-slate-800">
              {evaluation.completedAt ? new Date(evaluation.completedAt).toLocaleString() : "-"}
            </p>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-lg p-4">
            <p className="text-slate-600 text-sm">Team Score</p>
            <p className="text-3xl font-bold text-indigo-600 mt-1">
              {evaluation.overallScore ?? averageScore ?? "-"}
              <span className="text-base text-slate-500"> / 5</span>
            </p>
          </div>

          {evaluation.individualAvgScore !== null &&
            evaluation.individualAvgScore !== undefined && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-600 text-sm">Individual Avg</p>
                <p className="text-3xl font-bold text-slate-700 mt-1">
                  {evaluation.individualAvgScore.toFixed(2)}
                  <span className="text-base text-slate-500"> / 5</span>
                </p>
              </div>
            )}

          {scoreDifference !== null && (
            <div className={`rounded-lg p-4 ${scoreDifference >= 0 ? "bg-green-50" : "bg-red-50"}`}>
              <p className="text-slate-600 text-sm">Difference</p>
              <p
                className={`text-3xl font-bold mt-1 ${
                  scoreDifference >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {scoreDifference >= 0 ? "+" : ""}
                {scoreDifference.toFixed(2)}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Team {scoreDifference >= 0 ? "outperforms" : "underperforms"} individual average
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Scores */}
      <Card title="Scores by Criteria">
        <div className="divide-y divide-slate-200">
          {Object.entries(evaluation.scores || {}).map(([criteriaId, score]) => {
            const c = criteriaMap[criteriaId];
            return (
              <div key={criteriaId} className="py-3 grid md:grid-cols-4 gap-4 items-center">
                <div className="md:col-span-2">
                  <p className="font-semibold text-slate-900">{c?.name || criteriaId}</p>
                  {c?.description && <p className="text-sm text-slate-600 mt-1">{c.description}</p>}
                  {c?.category && (
                    <span className="mt-2 inline-block text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                      {c.category}
                    </span>
                  )}
                </div>
                <div className="font-mono text-lg text-slate-800">{score}</div>
                <div className="text-sm text-slate-500">Max: {c?.maxScore ?? 5}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Comments */}
      {evaluation.comments && (
        <Card title="Team Comments">
          <p className="text-slate-800 leading-relaxed whitespace-pre-line">
            {evaluation.comments}
          </p>
        </Card>
      )}

      {/* AI Insights */}
      <Card
        title="AI Insight"
        subtitle="Team dynamics, strengths, and improvement opportunities"
        actions={
          <button
            onClick={loadAi}
            disabled={aiLoading}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {aiLoading ? "Generating..." : "Generate AI Summary"}
          </button>
        }
      >
        {aiError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
            {aiError}
          </div>
        )}

        {!ai && !aiLoading && (
          <p className="text-sm text-slate-600">
            Click "Generate AI Summary" to view team insights.
          </p>
        )}

        {ai && (
          <div className="space-y-4">
            <div>
              <p className="text-slate-500 text-sm">Summary</p>
              <p className="text-slate-800 mt-1 leading-relaxed">{ai.summary}</p>
            </div>

            {ai.strengths && ai.strengths.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Team Strengths</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.strengths.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {ai.developmentAreas && ai.developmentAreas.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Areas for Improvement</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.developmentAreas.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {ai.recommendations && ai.recommendations.length > 0 && (
              <div>
                <p className="text-slate-500 text-sm">Recommendations</p>
                <ul className="list-disc list-inside text-slate-800 mt-1 space-y-1">
                  {ai.recommendations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

export default TeamEvaluationReport;
